import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { canPreviewFileGroup, filterArrayEmpty } from 'lib/riskbase';
import React from 'react';
import { Platform } from 'react-native';
import { getFileTypeFromUntrustedExtension } from 'service/file';
import { FileTypeGroupsEnum } from 'service/file/types';
import { File } from 'store/assets/types';

interface Props {
  files: Record<string, File>;
  fileIds: string[];
  placeholder?: string;
  nullOnNoFiles?: boolean;
  onPreview?: (fileId: string) => void;
  onDownload?: (fileId: string) => void;
  onRemove?: (fileId: string) => void;
  onPreviewText?: string;
  onRemoveText?: string;
  onDownloadText?: string;
}

export const FileDisplay: React.FC<Props> = ({
  files,
  fileIds,
  placeholder,
  nullOnNoFiles,
  onPreview,
  onDownload,
  onRemove,
  onPreviewText = 'Preview',
  onDownloadText = Platform.OS === 'web' ? 'Download' : 'Share',
  onRemoveText = 'Remove',
}) => {
  type DisplayFile = {
    id: string;
    name: string;
    typeName: string;
    group: FileTypeGroupsEnum;
  };

  const displayFiles = fileIds.length
    ? fileIds.reduce<DisplayFile[]>((acc, fileId) => {
        const file = files[fileId];
        if (file) {
          const fileType = getFileTypeFromUntrustedExtension(file.type);
          if (fileType) {
            acc.push({
              id: fileId,
              name: file.name,
              typeName: fileType.name,
              group: fileType.group,
            });
          }
        }
        return acc;
      }, [])
    : [];

  if (!displayFiles.length) {
    if (nullOnNoFiles) {
      return null;
    }
    return <CardRow value="" placeholder={placeholder || 'No Files'} />;
  }

  return (
    <ViewWithSeparators>
      {displayFiles.map((file) => (
        <CardRow
          key={file.id}
          label={file.name}
          value={file.typeName}
          valueStyle={{ minWidth: 50 }}
          showMenuOnContext
          showMenuOnPress
          hideMenuButton
          menuOptions={[
            onPreview && canPreviewFileGroup(file.group)
              ? {
                  text: onPreviewText,
                  onPress: () => onPreview(file.id),
                  icon: ICONS.EyeLight,
                }
              : undefined,
            onDownload
              ? {
                  text: onDownloadText,
                  onPress: () => onDownload(file.id),
                  icon: ICONS.ShareOS,
                }
              : undefined,
            onRemove
              ? {
                  text: onRemoveText,
                  onPress: () => onRemove(file.id),
                  destructive: true,
                }
              : undefined,
          ].filter(filterArrayEmpty)}
        />
      ))}
    </ViewWithSeparators>
  );
};
